import React, { useState } from 'react';
import { FormattedDate, FormattedMessage, FormattedNumber } from 'react-intl';
import { useMatch } from 'react-router';
import SimpleTooltip from '@rio-cloud/rio-uikit/SimpleTooltip';

import InternalErrorState from '@/features/app/InternalErrorState';
import { LoadingRow } from '@/features/app/LoadingRow';
import NotApplicable from '@/features/history/NotApplicable';
import { AggregatedMaintenanceHistoryList, MaintenanceHistoryRow, ServiceProduct } from '@/generated/GeneratedApiTypes';
import { MaintenanceHistoryParameters, useFetchMaintenanceHistoryQuery } from './api/maintenanceHistoryApi';
import CustomDateFilter from './CustomDateFilter';
import NoData from './NoData';

const translateComponent = (cId: number): React.ReactElement => {
    switch (cId) {
        case 1:
            return <FormattedMessage id="intl-msg:maintenanceHistory.component.engineOil" />;
        case 4:
            return <FormattedMessage id="intl-msg:maintenanceHistory.component.gearboxOil" />;
        case 5:
            return <FormattedMessage id="intl-msg:maintenanceHistory.component.axleOil" />;
        case 7:
            return <FormattedMessage id="intl-msg:maintenanceHistory.component.transferCaseOil" />;
        case 10:
            return <FormattedMessage id="intl-msg:maintenanceHistory.component.coolant" />;
        case 11:
            return <FormattedMessage id="intl-msg:maintenanceHistory.component.airFilter" />;
        case 12:
            return <FormattedMessage id="intl-msg:maintenanceHistory.component.airDryer" />;
        case 14:
            return <FormattedMessage id="intl-msg:maintenanceHistory.component.steeringFrontAxlesOil" />;
        case 15:
            return <FormattedMessage id="intl-msg:maintenanceHistory.component.steeringRearAxleOil" />;
        case 16:
            return <FormattedMessage id="intl-msg:maintenanceHistory.component.torqueConverterClutchOil" />;
        case 17:
            return <FormattedMessage id="intl-msg:maintenanceHistory.component.hydrodriveOil" />;
        case 18:
            return <FormattedMessage id="intl-msg:maintenanceHistory.component.valveClearance" />;
        case 19:
            return <FormattedMessage id="intl-msg:maintenanceHistory.component.hydrostaticFanOil" />;
        case 20:
            return <FormattedMessage id="intl-msg:maintenanceHistory.component.refrigerantCompressorOil" />;
        case 21:
            return <FormattedMessage id="intl-msg:maintenanceHistory.component.coolingComponent" />;
        case 32:
            return <FormattedMessage id="intl-msg:maintenanceHistory.component.annualInspection" />;

        default:
            return <FormattedMessage id="intl-msg:maintenanceHistory.component.invalidComponent" />;
    }
};

const isDangerValue = (value: number | undefined): boolean => {
    return value !== undefined && value > 0;
};

const getClassNameBasedOnValue = (value: number | undefined): string => {
    return isDangerValue(value) ? 'text-color-danger' : 'text-color-black';
};

const getClassNameRow = (row: MaintenanceHistoryRow, isCompactRow: boolean): string => {
    const rowClass = isCompactRow ? 'compactRow' : 'extendedRow';

    if (isDangerValue(row.overdue_in_pct) || isDangerValue(row.mileage_overdue) || isDangerValue(row.days_overdue)) {
        return rowClass + ' danger';
    } else {
        return rowClass + ' default';
    }
};

const getCellValueBasedOnValue = (
    value: number | undefined,
    isForDaysOverdue?: boolean,
): string | React.ReactElement => {
    if (value === undefined) {
        return <NoData />;
    }

    if (value <= 0) {
        return <FormattedMessage id="intl-msg:maintenanceHistory.component.notOverdue" />;
    }

    if (isForDaysOverdue) {
        return <FormattedMessage id="intl-msg:maintenanceHistory.component.daysOverdue" values={{ value }} />;
    }

    return <FormattedNumber value={value / 100} minimumFractionDigits={2} maximumFractionDigits={2} style="percent" />;
};

const LITRES = ' l';
const KILOMETERS = ' km';

const Title = () => (
    <div className="space-y-50">
        <p className={'text-size-h4'}>
            <FormattedMessage id="intl-msg:maintenanceHistory.title" />
        </p>
    </div>
);

const renderServiceProduct = (serviceProduct?: ServiceProduct) => {
    if (serviceProduct === undefined || serviceProduct.status === 'NO_DATA') {
        return <NoData />;
    }
    if (serviceProduct.status === 'NOT_APPLICABLE') {
        return <NotApplicable />;
    }

    return (
        <>
            <span>{serviceProduct.value}</span>
            <SimpleTooltip
                content={
                    <>
                        <p className={'margin-top-0'}>
                            <FormattedMessage id={'intl-msg:maintenanceHistory.serviceProduct.tooltip.header'} />
                        </p>
                        <div>
                            {serviceProduct.max_fuel_consumption_limit ? (
                                <FormattedMessage
                                    id={'intl-msg:maintenanceHistory.serviceProduct.maxFuelConsumption'}
                                    values={{
                                        fuel: (
                                            <FormattedNumber
                                                value={serviceProduct.max_fuel_consumption_limit}
                                                minimumFractionDigits={0}
                                                maximumFractionDigits={0}
                                            />
                                        ),
                                    }}
                                />
                            ) : (
                                <FormattedMessage
                                    id={'intl-msg:maintenanceHistory.serviceProduct.maxFuelConsumptionNoData'}
                                />
                            )}
                        </div>

                        <div>
                            {serviceProduct.max_mileage_limit ? (
                                <FormattedMessage
                                    id={'intl-msg:maintenanceHistory.serviceProduct.maxMileage'}
                                    values={{
                                        mileage: (
                                            <FormattedNumber
                                                value={serviceProduct.max_mileage_limit}
                                                minimumFractionDigits={0}
                                                maximumFractionDigits={0}
                                            />
                                        ),
                                    }}
                                />
                            ) : (
                                <FormattedMessage id={'intl-msg:maintenanceHistory.serviceProduct.maxMileageNoData'} />
                            )}
                        </div>

                        <div>
                            {serviceProduct.max_time_limit ? (
                                <FormattedMessage
                                    id={'intl-msg:maintenanceHistory.serviceProduct.maxRunningTime'}
                                    values={{
                                        time: (
                                            <FormattedNumber
                                                value={serviceProduct.max_time_limit}
                                                minimumFractionDigits={0}
                                                maximumFractionDigits={0}
                                            />
                                        ),
                                    }}
                                />
                            ) : (
                                <FormattedMessage
                                    id={'intl-msg:maintenanceHistory.serviceProduct.maxRunningTimeNoData'}
                                />
                            )}
                        </div>
                    </>
                }
                textAlignment={'left'}
            >
                <span
                    data-testid={'engine-oil-change-status-icon-tooltip'}
                    className="rioglyph rioglyph-info-sign text-size-16 margin-left-5"
                />
            </SimpleTooltip>
        </>
    );
};

const MaintenanceHistoryTable = () => {
    const [dateFrom, setDateFrom] = useState<string | undefined>(undefined);
    const [dateTo, setDateTo] = useState<string | undefined>(undefined);
    const assetId = useMatch('/:assetId')?.params.assetId;
    // no rows expanded by default
    const [expandedRows, setExpandedRows] = useState<number[]>([]);

    const params: MaintenanceHistoryParameters = {
        pathParameters: {
            assetId: assetId!,
        },
        queryParameters: {
            from: dateFrom,
            to: dateTo,
        },
    };

    const handleDateFilterChange = (from: string | undefined, to: string | undefined) => {
        setDateFrom(from);
        setDateTo(to);
    };

    // variable to control whether we are ready to show the data
    const isReadyToFetch = assetId !== undefined && dateFrom !== undefined && dateTo !== undefined;

    const { isLoading, isError, data, isFetching } = useFetchMaintenanceHistoryQuery(params, {
        skip: !isReadyToFetch,
    });

    if (isError) {
        return (
            <>
                <Title />
                <InternalErrorState
                    headlineI18n={'intl-msg:maintenanceHistory.history.error.headline'}
                    messageI18n={'intl-msg:maintenanceHistory.history.error.message'}
                />
            </>
        );
    }

    const handleToggleRow = (id: number) => {
        if (expandedRows.includes(id)) {
            setExpandedRows(expandedRows.filter((expanded) => expanded !== id));
        } else {
            setExpandedRows([...expandedRows, id]);
        }
    };

    const renderRow = (row: MaintenanceHistoryRow, isCompactedRow: boolean) => {
        return (
            <>
                {isCompactedRow ? <td>{translateComponent(row.component_id)}</td> : <td />}
                {isCompactedRow ? (
                    <td />
                ) : row.date_of_current_service ? (
                    <td>
                        <FormattedDate
                            value={row.date_of_current_service}
                            year="numeric"
                            month="numeric"
                            day="numeric"
                        />
                    </td>
                ) : (
                    <td>
                        <NoData />
                    </td>
                )}
                <td className={getClassNameBasedOnValue(row.overdue_in_pct)}>
                    {getCellValueBasedOnValue(row.overdue_in_pct)}
                </td>
                <td>
                    {row.fuel_consumption_since_previous_service ? (
                        <>
                            <FormattedNumber
                                value={row.fuel_consumption_since_previous_service}
                                minimumFractionDigits={0}
                                maximumFractionDigits={0}
                            />
                            {LITRES}
                        </>
                    ) : (
                        <NoData />
                    )}
                </td>
                <td className={getClassNameBasedOnValue(row.mileage_overdue)}>
                    {getCellValueBasedOnValue(row.mileage_overdue)}
                </td>
                <td>
                    {row.mileage_since_previous_service ? (
                        <>
                            <FormattedNumber
                                value={row.mileage_since_previous_service}
                                minimumFractionDigits={0}
                                maximumFractionDigits={0}
                            />
                            {KILOMETERS}
                        </>
                    ) : (
                        <NoData />
                    )}
                </td>
                <td className={getClassNameBasedOnValue(row.days_overdue)}>
                    {getCellValueBasedOnValue(row.days_overdue, true)}
                </td>
                <td>{renderServiceProduct(row.service_product)}</td>
            </>
        );
    };

    const renderTableBody = () => {
        if (isLoading || isFetching || !isReadyToFetch) {
            return (
                <>
                    <LoadingRow numCells={8} />
                    <LoadingRow numCells={8} />
                </>
            );
        } else {
            return (data as AggregatedMaintenanceHistoryList).map((componentHistory, index) => {
                const extendedRowHistory = componentHistory.slice(1);
                const rowKey = componentHistory[0].component_id;
                const isExpanded = expandedRows.includes(rowKey);
                return (
                    <>
                        <tr className={getClassNameRow(componentHistory[0], true)} key={rowKey + '-' + index}>
                            {renderRow(componentHistory[0], true)}
                            <td
                                className="table-action"
                                onClick={() => handleToggleRow(rowKey)}
                                data-testid={'expand-toggle-button-' + index}
                            >
                                <span>
                                    <span className="btn btn-muted btn-icon-only">
                                        <span
                                            className={`rioglyph ${
                                                isExpanded ? 'rioglyph-chevron-up' : 'rioglyph-chevron-down'
                                            }`}
                                        />
                                    </span>
                                </span>
                            </td>
                        </tr>

                        {isExpanded &&
                            extendedRowHistory.map((extendedRow: MaintenanceHistoryRow, extendedIndex: number) => (
                                <tr
                                    className={getClassNameRow(extendedRow, false)}
                                    key={extendedRow.component_id + '-' + extendedIndex}
                                >
                                    {renderRow(extendedRow, false)}
                                    <td className="table-action" />
                                </tr>
                            ))}
                    </>
                );
            });
        }
    };

    return (
        <>
            <Title />
            <div className={'margin-y-10 margin-bottom-15'}>
                <CustomDateFilter onDateChange={handleDateFilterChange} />
            </div>
            <div className={'table-responsive min-height-250'}>
                <table className={'table table-head-filled'}>
                    <thead>
                        <tr key={'maintenance-history-header'}>
                            <th>
                                <FormattedMessage id={'intl-msg:maintenanceHistory.table.header.component'} />
                            </th>
                            <th>
                                <FormattedMessage id={'intl-msg:maintenanceHistory.table.header.serviceDate'} />
                            </th>
                            <th>
                                <FormattedMessage
                                    id={'intl-msg:maintenanceHistory.table.header.overdueInConsumption'}
                                />
                            </th>
                            <th>
                                <FormattedMessage
                                    id={'intl-msg:maintenanceHistory.table.header.fuelConsumptionSinceLastService'}
                                />
                            </th>
                            <th>
                                <FormattedMessage id={'intl-msg:maintenanceHistory.table.header.overdueInMileage'} />
                            </th>
                            <th>
                                <FormattedMessage
                                    id={'intl-msg:maintenanceHistory.table.header.mileageSinceLastService'}
                                />
                            </th>
                            <th>
                                <FormattedMessage id={'intl-msg:maintenanceHistory.table.header.overdueInTime'} />
                            </th>
                            <th>
                                <FormattedMessage id={'intl-msg:maintenanceHistory.table.header.serviceProduct'} />
                            </th>
                            <th className="table-action" />
                        </tr>
                    </thead>
                    <tbody className={'bg-white'}>{renderTableBody()}</tbody>
                </table>
            </div>
        </>
    );
};
export default MaintenanceHistoryTable;
